/**
 * M-1 PR-d: AuthBridge para apps/public.
 *
 * Substitui o antigo AuthContext.shim.tsx (blue-green) e o AuthProvider
 * legado em AuthContext.tsx. Agora é o único Provider — não há mais
 * feature flag VITE_USE_PPS_AUTH nem fallback ao legado.
 *
 * Renderiza `<AuthProvider>` de @pps/auth-client com features
 * `broadcastSignOut` + `visibilityRehydrate` + `guardSpuriousSignOut`,
 * e o `LegacyContextBridge` republica os valores no Context exportado por
 * `./AuthContext.tsx`, mantendo intactos os 45+ call sites de `useAuth()`.
 *
 * Comportamentos preservados (replicados do legado):
 *   1) Sentry.setUser nas transições SIGNED_IN/SIGNED_OUT.
 *   2) Update fire-and-forget de `profiles.last_login_at` em SIGNED_IN.
 *   3) Insert fire-and-forget em `user_activity_logs` em SIGNED_IN.
 *   4) Sync de `i18n.language` com `profile.preferred_language` ao
 *      carregar o profile (dentro do `profileLoader`).
 *   5) `signUp(email, password, fullName?)` mantém assinatura legada — o
 *      bridge converte para a shape `{ fullName, redirectTo }` esperada
 *      pelo `@pps/auth-client`.
 */
import { useMemo, type ReactElement, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import {
  AuthProvider as PpsAuthProvider,
  isImpersonationTab,
  useAuth as usePpsAuth,
  type ProfileLoader,
  type OnAuthChange,
} from "@pps/auth-client";
import { supabase } from "@/integrations/supabase/client";
import i18n from "@/i18n";
import {
  AuthContext,
  type Profile,
  type AuthContextType,
} from "./AuthContext";

/**
 * Profile loader plugado no @pps/auth-client. Faz exatamente o mesmo SELECT
 * do legado e sincroniza `i18n.language` com `profile.preferred_language`.
 */
const loadProfile: ProfileLoader<Profile> = async (client, userId) => {
  const { data, error } = await client
    .from("profiles")
    .select(
      "id, user_id, full_name, avatar_url, level, preferred_sport_id, preferred_language, preferred_currency, country_code"
    )
    .eq("user_id", userId)
    .maybeSingle();

  if (error || !data) return null;

  if (data.preferred_language && data.preferred_language !== i18n.language) {
    i18n.changeLanguage(data.preferred_language);
  }

  return data as Profile;
};

/**
 * Callback `onAuthChange` plugado no @pps/auth-client. Replica os
 * side-effects que o AuthProvider legado fazia inline no listener:
 *   - Sentry.setUser em SIGNED_IN / null em SIGNED_OUT
 *   - last_login_at update + activity log insert em SIGNED_IN (fire-and-forget)
 */
const onAuthChange: OnAuthChange = (event, session) => {
  if (session?.user) {
    Sentry.setUser({
      id: session.user.id,
      email: session.user.email ?? undefined,
    });
  } else {
    Sentry.setUser(null);
  }

  // Aba de suporte (impersonação): o login é do ADMIN, não do usuário. Não
  // grava último acesso nem log de login — senão a saúde do clube e o
  // "último acesso" do owner mentem. A auditoria da sessão de suporte fica
  // em impersonation_sessions + admin_audit_logs.
  if (event === "SIGNED_IN" && session?.user && !isImpersonationTab()) {
    void supabase
      .from("profiles")
      .update({ last_login_at: new Date().toISOString() })
      .eq("user_id", session.user.id)
      .then(() => {});
    void supabase
      .from("user_activity_logs")
      .insert({ user_id: session.user.id, activity_type: "login" })
      .then(() => {});
  }
};

/**
 * Bridge: consome `useAuth` de @pps/auth-client e republica no Context
 * legado exportado por `./AuthContext.tsx`, preservando a API do hook
 * legado (signUp(email, password, fullName?) → metadata.fullName etc.).
 */
function LegacyContextBridge({ children }: { children: ReactNode }): ReactElement {
  const ctx = usePpsAuth<Profile>();

  const value = useMemo<AuthContextType>(
    () => ({
      user: ctx.user,
      session: ctx.session,
      profile: ctx.profile,
      loading: ctx.loading,
      signIn: async (email, password) => {
        const result = await ctx.signIn(email, password);
        return { error: result.error as Error | null };
      },
      signUp: async (email, password, fullName) => {
        const redirectTo = `${window.location.origin}/`;
        const result = await ctx.signUp(email, password, { fullName, redirectTo });
        return { error: result.error as Error | null };
      },
      signOut: async () => {
        await ctx.signOut();
      },
      refreshProfile: ctx.refreshProfile,
    }),
    [ctx]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Provider público. App.tsx importa `AuthProvider` daqui em vez do antigo
 * shim/legado. Único caminho — sem flag, sem branch.
 */
export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  return (
    <PpsAuthProvider<Profile>
      supabaseClient={supabase}
      profileLoader={loadProfile}
      features={{
        // Aba de suporte não participa do canal "pps-auth": o signOut dela
        // não pode derrubar as outras abas do origin, e o das outras não
        // pode derrubar a aba de suporte.
        broadcastSignOut: !isImpersonationTab(),
        visibilityRehydrate: true,
        guardSpuriousSignOut: true,
      }}
      onAuthChange={onAuthChange}
    >
      <LegacyContextBridge>{children}</LegacyContextBridge>
    </PpsAuthProvider>
  );
}
